
export default function ReasonsPartOne() {
  return (
    <div>
      {/* Reason 1 */}
      <section className="mb-16">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-teal-600">1</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Cash-Flow Visibility: The Lifeblood of Every El Paso Business</h2>
            <p className="text-gray-500 text-sm">Know exactly where every dollar goes—every single month</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
          <div>
            <p className="text-gray-600 leading-relaxed mb-4">
              Cash flow is the oxygen of a small business, and <strong>El Paso TX</strong> entrepreneurs feel this more acutely than most. Seasonal tourism swings around Sun Bowl events, back-to-school rushes near UTEP, and the cross-border shopping cycles with Ciudad Juarez all create revenue peaks and valleys that can catch owners off guard. Without monthly bookkeeping, you are essentially driving through the Franklin Mountains at night with your headlights off.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              When a professional bookkeeper closes your books every thirty days, you get a crystal-clear snapshot of money coming in versus money going out. You can see that your accounts receivable are aging past sixty days, that your supply costs jumped twelve percent since last quarter, or that a particular service line is quietly bleeding cash. These are insights you simply cannot get from glancing at your bank balance on a phone app.
            </p>
            <p className="text-gray-600 leading-relaxed">
              A recent survey by the U.S. Bank found that <strong>82 percent of small businesses that fail cite cash-flow problems</strong> as the primary cause. Monthly bookkeeping is the most reliable way to make sure your El Paso business never joins that statistic. It transforms raw transaction data into a living, breathing financial dashboard you can act on immediately.
            </p>
          </div>
          <div className="w-full h-72 rounded-xl overflow-hidden shadow-lg">
            <img
              src="https://public.readdy.ai/ai/img_res/1e06daaa7bef57aed0cff2e25624cafa.jpg"
              alt="cash-flow-visibility-monthly-bookkeeping-el-paso"
              title="Cash Flow Visibility Through Monthly Bookkeeping in El Paso TX"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Reason 2 */}
      <section className="mb-16">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-orange-500">2</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Tax Readiness All Year Long—Not Just in April</h2>
            <p className="text-gray-500 text-sm">Stop scrambling and start saving at tax time</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
          <div className="w-full h-72 rounded-xl overflow-hidden shadow-lg order-2 md:order-1">
            <img
              src="https://readdy.ai/api/search-image?query=Organized%20tax%20documents%20and%20folders%20neatly%20arranged%20on%20a%20clean%20white%20desk%20with%20a%20calendar%20showing%20April%20highlighted%2C%20laptop%20displaying%20tax%20preparation%20software%2C%20green%20plant%20and%20warm%20desk%20lamp%2C%20professional%20tax%20readiness%20concept%20with%20warm%20earth%20tones%20and%20teal%20accents&width=600&height=450&seq=monthly-bk-taxready-ep-003&orientation=landscape"
              alt="year-round-tax-readiness-el-paso-small-business"
              title="Year-Round Tax Readiness for El Paso TX Small Businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-gray-600 leading-relaxed mb-4">
              Every January, accountants across <strong>El Paso TX</strong> brace themselves for the annual flood of panicked business owners carrying shoeboxes full of receipts. The frantic rush to reconstruct twelve months of financial activity in a few weeks leads to missed deductions, filing errors, and unnecessary stress. This is one of the clearest reasons <strong>why El Paso small businesses need monthly bookkeeping</strong>.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              When your books are reconciled and categorized every month, tax preparation becomes a straightforward handoff rather than an archaeological dig. Your tax preparer receives clean, organized records that allow them to maximize every legitimate deduction—from mileage driven between job sites in the Lower Valley to the home-office expenses of your remote team members.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Texas does not levy a personal income tax, but the <strong>Texas Franchise Tax</strong> still applies to most businesses with revenue above the threshold. Monthly bookkeeping ensures you are tracking the right revenue figures and filing deadlines so you never face penalties. Quarterly estimated federal tax payments also become painless when your profit numbers are already calculated and waiting.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-500 p-4 rounded-r-lg">
              <p className="text-teal-800 font-medium text-sm">
                <i className="ri-lightbulb-line mr-2"></i>
                <strong>Pro Tip:</strong> Businesses that maintain monthly books typically save between $1,200 and $3,000 per year in tax-preparation fees alone because their accountant spends less time sorting and correcting records.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Reason 3 */}
      <section className="mb-16">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-red-500">3</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Catching Errors, Fraud, and Duplicate Charges Early</h2>
            <p className="text-gray-500 text-sm">A thirty-day window beats a twelve-month mystery</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
          <div>
            <p className="text-gray-600 leading-relaxed mb-4">
              Mistakes happen. Vendors accidentally double-bill you. Credit-card processors apply incorrect fees. Employees occasionally miscategorize an expense. And in worst-case scenarios, internal theft goes undetected for months. Monthly bookkeeping acts as a built-in alarm system that catches these issues while they are still small and fixable.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Consider a real scenario we encountered with an <strong>El Paso TX</strong> restaurant owner. A food distributor had been charging a three-percent surcharge that was not part of the original contract. Because the owner only reviewed finances once a year, the overcharges accumulated to more than $4,600 before anyone noticed. With monthly reconciliation, that discrepancy would have surfaced within the first thirty days—saving thousands of dollars and a lot of frustration.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Bank reconciliation, vendor statement matching, and payroll audits are all standard components of a monthly bookkeeping routine. Each one serves as a checkpoint that keeps your financial data honest and your hard-earned revenue where it belongs—in your business account.
            </p>
          </div>
          <div className="w-full h-72 rounded-xl overflow-hidden shadow-lg">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20accountant%20using%20magnifying%20glass%20to%20examine%20financial%20documents%20and%20invoices%20for%20errors%2C%20modern%20office%20desk%20with%20dual%20monitors%20showing%20spreadsheet%20data%2C%20warm%20lighting%20with%20organized%20paperwork%2C%20fraud%20detection%20and%20error%20checking%20concept%20with%20teal%20and%20warm%20accents&width=600&height=450&seq=monthly-bk-errors-ep-004&orientation=landscape"
              alt="catching-bookkeeping-errors-fraud-el-paso-tx"
              title="Catching Financial Errors and Fraud Early With Monthly Bookkeeping"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Reason 4 */}
      <section className="mb-16">
        <div className="flex items-start gap-6 mb-6">
          <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold text-yellow-600">4</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Better Loan and Credit Applications</h2>
            <p className="text-gray-500 text-sm">Lenders love clean books—and reward them with better terms</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 items-center mb-8">
          <div className="w-full h-72 rounded-xl overflow-hidden shadow-lg order-2 md:order-1">
            <img
              src="https://readdy.ai/api/search-image?query=Business%20owner%20and%20bank%20loan%20officer%20reviewing%20financial%20statements%20together%20at%20a%20modern%20bank%20office%20desk%2C%20professional%20handshake%20moment%20with%20approved%20loan%20documents%20visible%2C%20warm%20natural%20lighting%2C%20clean%20professional%20setting%20with%20teal%20and%20earth%20tone%20accents%2C%20El%20Paso%20Texas%20business%20district%20visible%20through%20window&width=600&height=450&seq=monthly-bk-loans-ep-005&orientation=landscape"
              alt="better-loan-applications-monthly-bookkeeping-el-paso"
              title="Stronger Loan Applications With Monthly Bookkeeping in El Paso TX"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="order-1 md:order-2">
            <p className="text-gray-600 leading-relaxed mb-4">
              At some point, nearly every growing <strong>El Paso</strong> business needs outside capital—whether it is an SBA loan to expand into a second location, a line of credit to cover seasonal inventory, or equipment financing for a new fleet vehicle. The first thing any lender asks for is up-to-date financial statements: a profit-and-loss report, a balance sheet, and a cash-flow statement.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              If your books are months behind, you either delay the application while you scramble to catch up or you submit incomplete records that raise red flags. Neither outcome is good. Monthly bookkeeping means your financials are always investor-ready and lender-ready, giving you the ability to act fast when opportunity knocks.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Local institutions like WestStar Bank, El Paso Water Utilities business programs, and Borderplex Alliance grant opportunities all require documented financial history. Businesses with clean monthly records consistently receive faster approvals and more favorable interest rates because they demonstrate financial discipline and transparency.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
